import { Box, ButtonBase, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

export type PillTab<T> = Readonly<{
    value: T
    label: string
}>

type StandardPillTabsProps<T> = Readonly<{
    tabs: ReadonlyArray<PillTab<T>>
    value: T
    onChange: (value: T) => void
    ariaLabel?: string
    className?: string
}>

const useStyles = makeStyles()((theme) => ({
    container: {
        display: "inline-flex",
        alignItems: "center",
        gap: ".125rem",
        padding: ".25rem",
        borderRadius: ".75rem",
        background: theme.palette.tailwind.blue[100],
        maxWidth: "100%",
        overflowX: "auto",
        "&::-webkit-scrollbar": {
            display: "none",
        },
    },
    pill: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: ".5rem .875rem",
        borderRadius: ".75rem",
        color: theme.palette.text.secondary,
        whiteSpace: "nowrap",
        flexShrink: 0,
        transition: "background .15s, color .15s",
        "&:hover": {
            background: theme.palette.tailwind.slate[200],
        },
    },
    pillSelected: {
        background: theme.palette.tailwind.slate[900],
        color: theme.palette.common.white,
        "&:hover": {
            background: theme.palette.tailwind.slate[800],
        },
    },
    label: {
        fontSize: ".875rem",
        fontWeight: 600,
        lineHeight: 1.25,
        color: "inherit",
    },
}))

/**
 * Segmented pill selector: a light rounded container holding pills, the active one rendered as a dark chip
 * with a white label (the day-pill look of `TakeawayDateBar`). Used for table filters — forms use the
 * underline `StandardFormTabs` instead.
 */
export function StandardPillTabs<T>({ tabs, value, onChange, ariaLabel, className }: StandardPillTabsProps<T>) {
    const { classes, cx } = useStyles()

    return (
        <Box className={cx(classes.container, className)} role="tablist" aria-label={ariaLabel}>
            {tabs.map((tab, index) => {
                const selected = tab.value === value

                return (
                    <ButtonBase
                        key={`${index}-${tab.label}`}
                        role="tab"
                        aria-selected={selected}
                        className={cx(classes.pill, selected && classes.pillSelected)}
                        onClick={() => onChange(tab.value)}
                        disableRipple
                    >
                        <Typography component="span" className={classes.label}>
                            {tab.label}
                        </Typography>
                    </ButtonBase>
                )
            })}
        </Box>
    )
}
